import React, { useCallback, useMemo, useRef, useState } from "react";
import { EmptyStateErro } from "../../components/EmptyState";
import { motion } from "framer-motion";
import {
  FiCheckCircle,
  FiClock,
  FiDroplet,
  FiEdit3,
  FiFileText,
  FiHome,
  FiPackage,
  FiScissors,
  FiShoppingCart,
  FiThumbsUp,
  FiTool,
  FiTrendingUp,
  FiTruck,
  FiUsers,
} from "react-icons/fi";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { useParametrosTOC } from "../../hooks/useParametrosTOC";
import { useTOC } from "../../hooks/useTOC";
import { addDiasUteis } from "../../utils/calculos";
import { isNo } from "../../utils/data";
import {
  estaEmAprovacao,
  estaEmRevisao,
  estaNaFilaDoPpcp,
  limiteDaRevisao,
} from "../../utils/fluxoExecutivo";
import { F } from "../../utils/gerencialFields";
import {
  getIndustrializacaoDeadline,
  isIndustrializacaoPending,
} from "../../utils/industrializacao";
import {
  buildLiberacaoStatus,
  isLiberacaoAmbientePending,
} from "../../utils/liberacaoAmbientes";
import { getMarcenariaDeadline, isMarcenariaPending } from "../../utils/marcenaria";
import { isBeforeToday, toDate } from "../../utils/status";
import { ETAPA, getEtapaAtual } from "../../utils/toc/etapas";
import { prazoTerceiros, temItensTerceirosPendentes } from "../../utils/terceiros";
import AlertaIdentidade from "./AlertaIdentidade";
import AtalhosPorEtapa from "./AtalhosPorEtapa";
import CommandPanel from "./CommandPanel";
import CriticoDrawer from "./CriticoDrawer";
import CriticosList from "./CriticosList";
import WipFluxoBar from "./WipFluxoBar";
import {
  buildNextAction,
  formatDataExtenso,
  formatSaudacao,
} from "./helpers";
import { Greeting, PageState, PageTitle, Wrapper } from "./styles";

function deadlineEntrega(row) {
  return row[F.DATA_REPROGRAMADA] || row[F.ENTREGA_PREVISTA];
}

const PAINEIS = [
  {
    id: "liberacao",
    label: "Liberação",
    rota: "/liberacao",
    icon: FiHome,
    filtro: isLiberacaoAmbientePending,
    atraso: (row) => buildLiberacaoStatus(row).statusVariant === "danger",
  },
  {
    id: "acabamentos",
    label: "Definição de Acabamentos",
    rota: "/acabamentos",
    icon: FiCheckCircle,
    filtro: (row) =>
      Boolean(row[F.DEFINICAO_ACABAMENTOS_LIMITE]) &&
      !row[F.DEFINICAO_ACABAMENTOS_REALIZADO],
    atraso: (row) => isBeforeToday(row[F.DEFINICAO_ACABAMENTOS_LIMITE]),
  },
  {
    id: "medicao",
    label: "Medição",
    rota: "/medicao",
    icon: FiClock,
    filtro: (row) =>
      isNo(row[F.OBRA_FUTURA]) &&
      Boolean(row[F.LIBERACAO_DA_OBRA]) &&
      !row[F.MEDICAO_REALIZADO],
    atraso: (row) => isBeforeToday(row[F.MEDICAO_LIMITE]),
  },
  {
    id: "executivo",
    label: "Executivo",
    rota: "/executivo",
    icon: FiFileText,
    filtro: (row) =>
      isNo(row[F.OBRA_FUTURA]) &&
      Boolean(row[F.LIBERACAO_DA_OBRA]) &&
      Boolean(row[F.DEFINICAO_ACABAMENTOS_REALIZADO]) &&
      Boolean(row[F.MEDICAO_REALIZADO]) &&
      !row[F.EXECUTIVO_REALIZADO],
    atraso: (row) => isBeforeToday(row[F.EXECUTIVO_LIMITE]),
  },
  // Revisão e Aprovação seguem a ordem de cada linha e usam os mesmos filtros
  // das telas (utils/fluxoExecutivo), para o número do card bater com a lista.
  {
    id: "revisao",
    label: "Revisão",
    rota: "/revisao",
    icon: FiEdit3,
    filtro: estaEmRevisao,
    atraso: (row) => isBeforeToday(limiteDaRevisao(row)),
  },
  {
    id: "aprovacao",
    label: "Aprovação",
    rota: "/aprovacao",
    icon: FiThumbsUp,
    filtro: estaEmAprovacao,
    atraso: (row) => isBeforeToday(row[F.APROVACAO_LIMITE]),
  },
  {
    id: "ppcp",
    label: "PPCP",
    rota: "/ppcp",
    icon: FiTrendingUp,
    filtro: estaNaFilaDoPpcp,
    atraso: (row) => {
      const aprovacao = row[F.APROVACAO_DO_EXECUTIVO];
      const limite = aprovacao ? addDiasUteis(aprovacao, 5) : null;
      return isBeforeToday(limite);
    },
  },
  {
    id: "compras",
    label: "Compras",
    rota: "/compras",
    icon: FiShoppingCart,
    filtro: (row) =>
      Boolean(row[F.OTIMIZACAO_DO_LOTE]) &&
      Boolean(row[F.NUMERO_DO_LOTE]) &&
      !row[F.MDF_BORDO],
    atraso: (row) => isBeforeToday(deadlineEntrega(row)),
  },
  {
    id: "ferragens",
    label: "Ferragens",
    rota: "/ferragens",
    icon: FiPackage,
    filtro: (row) =>
      Boolean(row[F.OTIMIZACAO_DO_LOTE]) &&
      Boolean(row[F.NUMERO_DO_LOTE]) &&
      !row[F.INSUMOS_FERRAGENS],
    atraso: (row) => isBeforeToday(deadlineEntrega(row)),
  },
  {
    id: "industrializacao",
    label: "Máquinas",
    rota: "/industrializacao",
    icon: FiScissors,
    filtro: isIndustrializacaoPending,
    atraso: (row) => isBeforeToday(getIndustrializacaoDeadline(row)),
  },
  {
    id: "terceiros",
    label: "Terceiros",
    rota: "/terceiros",
    icon: FiUsers,
    // Mesma regra da tela /terceiros: item de terceiro sem data, inclusive de
    // ambiente ja carregado; atraso pelo prazo do terceiro (ENTREGA TERCEIROS).
    filtro: temItensTerceirosPendentes,
    atraso: (row) => isBeforeToday(prazoTerceiros(row)),
  },
  {
    id: "marcenaria",
    label: "Marcenaria",
    rota: "/marcenaria",
    icon: FiTool,
    filtro: isMarcenariaPending,
    atraso: (row) => isBeforeToday(getMarcenariaDeadline(row)),
  },
  {
    id: "acabamento",
    label: "Acabamento",
    rota: "/acabamento",
    icon: FiDroplet,
    filtro: (row) => getEtapaAtual(row) === ETAPA.ACABAMENTO,
    atraso: (row) => isBeforeToday(deadlineEntrega(row)),
  },
  {
    id: "entregas",
    label: "Entregas",
    rota: "/entregas",
    icon: FiTruck,
    filtro: (row) =>
      isNo(row[F.OBRA_FUTURA]) &&
      Boolean(row[F.OTIMIZACAO_DO_LOTE]) &&
      !row[F.CARREGAMENTO],
    atraso: (row) => isBeforeToday(deadlineEntrega(row)),
  },
  {
    id: "obras",
    label: "Histórico de Entregas",
    rota: "/obras",
    icon: FiCheckCircle,
    filtro: (row) => Boolean(row[F.CARREGAMENTO]),
    atraso: (row) => {
      const entregaContrato = toDate(row[F.ENTREGA_PREVISTA]);
      const carregamento = toDate(row[F.CARREGAMENTO]);
      return Boolean(
        entregaContrato && carregamento && carregamento > entregaContrato
      );
    },
  },
];

export default function Home() {
  const { dados, erro, initialLoading } = useDados();
  const { precosVisiveis } = usePrecosVisiveis();
  const { parametros } = useParametrosTOC();
  const criticosRef = useRef(null);
  const [criticoSelecionado, setCriticoSelecionado] = useState(null);

  const ativos = useMemo(
    () => (Array.isArray(dados) ? dados.filter((row) => isNo(row[F.OBRA_FUTURA])) : []),
    [dados]
  );

  const toc = useTOC(ativos, parametros);

  const paineisComContagem = useMemo(() => {
    if (!dados.length) return [];
    return PAINEIS.map((painel) => {
      const itens = dados.filter(painel.filtro);
      const atrasados = itens.filter(painel.atraso).length;
      return { ...painel, total: itens.length, atrasados };
    });
  }, [dados]);

  const atrasadosGlobal = useMemo(() => {
    return ativos.filter((row) => {
      const dataLimite = deadlineEntrega(row);
      return dataLimite && !row[F.CARREGAMENTO] && isBeforeToday(dataLimite);
    }).length;
  }, [ativos]);

  const nextAction = useMemo(
    () =>
      buildNextAction(toc, {
        calibracaoCompleta: parametros.calibracaoCompleta,
        atrasadosGlobal,
      }),
    [toc, parametros.calibracaoCompleta, atrasadosGlobal]
  );

  const scrollToCriticos = useCallback(() => {
    criticosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (initialLoading) return <PageLoader scope="home" />;
  if (erro) return <EmptyStateErro erro={erro} />;
  if (!dados.length) return <PageState>Sem dados disponíveis.</PageState>;

  return (
    <Wrapper>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <PageTitle>Início</PageTitle>
        <Greeting>
          {formatSaudacao()} · {formatDataExtenso()}
        </Greeting>

        <AlertaIdentidade dados={dados} />

        <CommandPanel
          toc={toc}
          calibracaoCompleta={parametros.calibracaoCompleta}
          precosVisiveis={precosVisiveis}
          nextAction={nextAction}
          onScrollCriticos={scrollToCriticos}
        />

        <WipFluxoBar
          wipPorEtapa={toc.wipPorEtapa}
          gargaloEtapa={toc.gargalo?.etapa}
          calibracaoCompleta={parametros.calibracaoCompleta}
          precosVisiveis={precosVisiveis}
        />

        <CriticosList
          listRef={criticosRef}
          criticos={toc.criticos}
          onSelect={setCriticoSelecionado}
        />

        <AtalhosPorEtapa
          paineis={paineisComContagem}
          gargaloEtapa={toc.gargalo?.etapa}
          calibracaoCompleta={parametros.calibracaoCompleta}
        />
      </motion.div>

      {criticoSelecionado && (
        <CriticoDrawer
          critico={criticoSelecionado}
          precosVisiveis={precosVisiveis}
          onClose={() => setCriticoSelecionado(null)}
        />
      )}
    </Wrapper>
  );
}
